import {
  Add,
  Delete,
  Edit,
  MoreVert,
  TrendingUp
} from '@mui/icons-material';
import {
  Badge,
  Box,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Typography,
  Button
} from '@mui/material';
import React from 'react';
import { Droppable } from 'react-beautiful-dnd';
import DealCard from './DealCard';

const PipelineStage = ({
  stage,
  deals = [],
  onDealDrop,
  onDealEdit,
  onDealDelete,
  onDealView,
  onAddDeal,
  onEditStage,
  onDeleteStage,
  clients = [],
  products = [],
  leads = [],
  isLoading = false
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);


  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditStage = () => {
    handleMenuClose();
    if (onEditStage) onEditStage(stage);
  };

  const handleDeleteStage = () => {
    handleMenuClose();
    if (onDeleteStage) onDeleteStage(stage);
  };

  const handleAddDeal = () => {
    if (onAddDeal) onAddDeal(stage);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const totalAmount = deals.reduce((sum, deal) => sum + (deal.amount || 0), 0);
  const dealsCount = deals.length;

  return (
    <Paper
      sx={{
        width: { xs: 280, sm: 300, md: 320 },
        minWidth: { xs: 280, sm: 300, md: 320 },
        minHeight: 600,
        p: 2,
        background: 'rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        borderRadius: '16px',
        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.3s ease',
        flex: '0 0 auto'
      }}
    >
      {/* Заголовок этапа */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: stage.color || '#3B82F6',
                mr: 1,
                flexShrink: 0
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                color: 'white',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1
              }}
            >
              {stage.name}
            </Typography>
            <Badge
              badgeContent={dealsCount}
              color="primary"
              sx={{
                ml: 1,
                '& .MuiBadge-badge': {
                  background: stage.color || '#3B82F6',
                  color: 'white'
                }
              }}
            />
          </Box>

          <IconButton
            size="small"
            onClick={handleMenuClick}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': { color: 'white' }
            }}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </Box>

        {/* Статистика этапа */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TrendingUp sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.7)', mr: 0.5 }} />
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              {formatCurrency(totalAmount)}
            </Typography>
          </Box>

          {stage.probability !== undefined && (
            <Chip
              label={`${stage.probability}%`}
              size="small"
              sx={{
                background: `${stage.color}20` || 'rgba(59, 130, 246, 0.2)',
                color: stage.color || '#3B82F6',
                fontWeight: 'bold',
                fontSize: '0.7rem'
              }}
            />
          )}
        </Box>

        {stage.description && (
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '0.8rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {stage.description}
          </Typography>
        )}

        <Divider sx={{ mt: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
      </Box>

      {/* Кнопка добавления сделки */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color="success"
          startIcon={<Add />}
          fullWidth
          onClick={handleAddDeal}
        >
          Добавить сделку
        </Button>
      </Box>

      {/* Список сделок */}
      <Droppable droppableId={stage.id}>
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              maxHeight: { xs: 'calc(100vh - 340px)', md: 'calc(100vh - 300px)' },
              overflowY: 'auto',
              backgroundColor: snapshot.isDraggingOver
                ? 'rgba(59, 130, 246, 0.1)'
                : 'transparent',
              borderRadius: '8px',
              transition: 'background-color 0.2s ease',
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '2px',
              },
            }}
          >
            {isLoading ? (
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', textAlign: 'center', py: 2 }}>
                Загрузка...
              </Typography>
            ) : deals.length === 0 ? (
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', textAlign: 'center', py: 2 }}>
                Нет сделок в этом этапе
              </Typography>
            ) : (
              deals.map((deal, index) => (
                <DealCard
                  key={deal.id}
                  deal={deal}
                  index={index}
                  clients={clients}
                  products={products}
                  leads={leads}
                  onEdit={onDealEdit}
                  onDelete={onDealDelete}
                  onView={onDealView}
                />
              ))
            )}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>

      {/* Контекстное меню этапа */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            background: 'rgba(30, 30, 30, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }
        }}
      >
        <MenuItem onClick={handleEditStage} sx={{ color: 'white' }}>
          <Edit sx={{ mr: 1, fontSize: 18 }} />
          Редактировать этап
        </MenuItem>
        <MenuItem onClick={handleDeleteStage} sx={{ color: '#EF4444' }}>
          <Delete sx={{ mr: 1, fontSize: 18 }} />
          Удалить этап
        </MenuItem>
      </Menu>

    </Paper>
  );
};

export default PipelineStage;
